import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  Connection,
  Edge,
  Node,
  useReactFlow,
  Panel
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../../convex/_generated/api';
import { Id } from '../../../../../convex/_generated/dataModel';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { Button } from '@/components/ui/button';
import { Save, ArrowLeft, Archive } from 'lucide-react';
import { toast } from 'sonner';

import { TriggerNode } from '@/components/workflows/custom-builder/TriggerNode';
import { ActionNode } from '@/components/workflows/custom-builder/ActionNode';
import { DelayNode } from '@/components/workflows/custom-builder/DelayNode';
import { Palette } from '@/components/workflows/custom-builder/Palette';
import { PropertySidebar } from '@/components/workflows/custom-builder/PropertySidebar';

const nodeTypes = {
  triggerNode: TriggerNode,
  actionNode: ActionNode,
  delayNode: DelayNode,
};

let id = 0;
const getId = () => `dndnode_${id++}`;

function BuilderFlow() {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { screenToFlowPosition } = useReactFlow();
  
  const [searchParams] = useSearchParams();
  const workflowId = searchParams.get('id') as Id<"workflows"> | null;
  const navigate = useNavigate();

  const getWorkflow = useQuery(api.workflows.get, workflowId ? { id: workflowId } : "skip");
  const createWorkflow = useMutation(api.workflows.create);
  const updateWorkflow = useMutation(api.workflows.update);
  const archiveWorkflow = useMutation(api.workflows.archive);

  const [workflowName, setWorkflowName] = useState("Untitled Workflow");
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);

  // Initialize from DB
  useEffect(() => {
    if (getWorkflow) {
      setNodes(getWorkflow.nodes || []);
      setEdges(getWorkflow.edges || []);
      setWorkflowName(getWorkflow.name || "Untitled Workflow");
    }
  }, [getWorkflow, setNodes, setEdges]);

  const onConnect = useCallback(
    (params: Connection | Edge) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');

      if (typeof type === 'undefined' || !type) {
        return;
      }

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      
      const newNode: Node = {
        id: getId(),
        type,
        position,
        data: { label: `${type} node`, config: {} },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [screenToFlowPosition, setNodes]
  );

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const updateNodeData = useCallback((nodeId: string, data: any) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          node.data = { ...node.data, ...data };
          setSelectedNode(node); // Update the selected node state to re-render sidebar
        }
        return node;
      })
    );
  }, [setNodes]);

  const handleSave = async () => {
    try {
      if (workflowId) {
        await updateWorkflow({
          id: workflowId,
          name: workflowName,
          nodes,
          edges,
        });
        toast.success("Workflow saved!");
      } else {
        const newId = await createWorkflow({
          name: workflowName,
          nodes,
          edges,
        });
        toast.success("Workflow created!");
        navigate(`/workflows/builder?id=${newId}`);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to save workflow");
    }
  };

  const handleArchive = async () => {
    if (!workflowId) return;
    try {
      await archiveWorkflow({ id: workflowId });
      toast.success("Workflow archived");
      navigate("/workflows");
    } catch (e) {
      console.error(e);
      toast.error("Failed to archive workflow");
    }
  };

  return (
    <div className="flex h-full w-full relative">
      <Palette />
      <div className="flex-1 h-full" ref={reactFlowWrapper}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          fitView
        >
          <Background color="#ccc" gap={16} />
          <Controls />
          <Panel position="top-right" className="flex gap-2">
            <input 
              className="px-3 py-2 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              placeholder="Workflow Name"
            />
            {workflowId && (
              <Button onClick={handleArchive} variant="outline" className="gap-2">
                <Archive className="h-4 w-4" /> Archive
              </Button>
            )}
            <Button onClick={handleSave} className="gap-2">
              <Save className="h-4 w-4" /> Save
            </Button>
          </Panel>
        </ReactFlow>
      </div>
      {selectedNode && (
        <PropertySidebar 
          node={selectedNode} 
          updateNodeData={updateNodeData}
          onClose={() => setSelectedNode(null)} 
        />
      )}
    </div>
  );
}

export default function WorkflowBuilderPage() {
  return (
    <DashboardLayout activeTab="Workflows">
      <div className="h-[calc(100vh-4rem)] -m-4 sm:-m-6 flex flex-col">
        <div className="bg-white border-b px-6 py-3 flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <a href="/workflows">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Workflows
            </a>
          </Button>
          <div className="h-4 w-px bg-gray-300" />
          <span className="font-semibold text-gray-700">Advanced Workflow Builder</span>
        </div>
        <div className="flex-1 relative">
          <ReactFlowProvider>
            <BuilderFlow />
          </ReactFlowProvider>
        </div>
      </div>
    </DashboardLayout>
  );
}
